import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DealForgeLogo from "../components/DealForgeLogo";
import {
  ArrowRight,
  Check,
  Zap,
  Target,
  Calculator,
  Users,
  Workflow,
  Building2,
  Shield,
  Layers,
  HardHat,
  Landmark,
  Home,
  Briefcase,
  TrendingUp,
  Clock,
  Lock
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function WhatIsDealForgePage() {
  const problems = [
    {
      icon: Clock,
      title: "Execution Bottlenecks",
      description: "Deals die in the gap between acquisition and closing. DealForge eliminates coordination overhead."
    },
    {
      icon: Workflow,
      title: "Disconnected Systems",
      description: "Spreadsheets, CRMs, doc tools, lender apps—none talk to each other. DealForge infrastructure unifies everything."
    },
    {
      icon: Users,
      title: "Fragmented Teams",
      description: "Contractors, lenders, title companies, realtors all in different apps. DealForge connects your entire deal ecosystem."
    },
    {
      icon: TrendingUp,
      title: "Scaling Chaos",
      description: "What works for 5 deals breaks at 50. DealForge infrastructure scales with you."
    }
  ];

  const features = [
    {
      icon: Layers,
      title: "End-to-End Deal Lifecycle",
      description: "DealForge manages every stage from lead to close to post-close in one unified system."
    },
    {
      icon: Calculator,
      title: "Creative Finance Engine",
      description: "Subject-To, Seller Finance, Wraps—DealForge handles complex deal structures with accurate math."
    },
    {
      icon: HardHat,
      title: "Contractor Marketplace",
      description: "Verified contractors submit bids directly into your DealForge underwriting."
    },
    {
      icon: Landmark,
      title: "Lender Network",
      description: "Hard money and private lenders compete for your deals through DealForge capital infrastructure."
    },
    {
      icon: Target,
      title: "Decision Support",
      description: "DealForge AI provides confidence scores, risk flags, and next-action prompts."
    },
    {
      icon: Lock,
      title: "Single Source of Truth",
      description: "Every file, photo, contract, and note locked to the deal in DealForge—never scattered again."
    }
  ];

  const roles = [
    { icon: Briefcase, title: "Investors & Wholesalers", desc: "DealForge is your command center for acquisitions and dispositions." },
    { icon: Home, title: "Realtors", desc: "Execute transactions and coordinate closings through DealForge." },
    { icon: Landmark, title: "Lenders", desc: "Source deal flow and place capital via the DealForge network." },
    { icon: HardHat, title: "Contractors", desc: "Win rehab bids and manage projects in DealForge." },
    { icon: Building2, title: "Title Companies", desc: "Offer closing services through the DealForge marketplace." }
  ];

  return (
    <>
      <Helmet>
        <title>What is DealForge™? | Real Estate Deal Infrastructure</title>
        <meta name="description" content="DealForge™ is real estate deal infrastructure—not just another tool. Learn how DealForge connects acquisition, capital, execution, and closing in one unified system." />
        <meta property="og:title" content="What is DealForge™? | Real Estate Deal Infrastructure" />
        <meta property="og:description" content="DealForge™ is real estate deal infrastructure—not just another tool. Learn how DealForge connects acquisition, capital, execution, and closing." />
        <link rel="canonical" href="https://dealforge.pro/what-is-dealforge" />
      </Helmet>

      <div className="min-h-screen bg-black">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/">
                <DealForgeLogo size="md" showTM={true} />
              </Link>
              <div className="flex items-center gap-6">
                <Link to="/" className="text-white/50 hover:text-white transition-all">
                  Home
                </Link>
                <Link to="/login" className="text-white/50 hover:text-white transition-all">
                  Login
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 relative overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />
          
          <div className="max-w-4xl mx-auto text-center relative">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              What is <span className="text-[#D4AF37]">DealForge</span>™?
            </h1>
            <p className="text-xl md:text-2xl text-white/70 mb-8 leading-relaxed">
              DealForge is <strong className="text-white">real estate deal infrastructure</strong>—not just another tool or CRM.
              It's the execution layer that connects acquisition, capital, execution, and closing in one system.
            </p>
            <p className="text-lg text-white/50 max-w-3xl mx-auto mb-10">
              The DealForge platform eliminates the coordination overhead that kills deals. 
              Built for operators doing 10+ deals per year who need infrastructure that scales.
            </p>
            <Link to="/">
              <Button className="gold-gradient-btn h-14 px-10 text-lg font-bold">
                Apply for DealForge Access
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* The Problem Section */}
        <section className="py-20 px-4 border-t border-white/[0.06]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                The Problem DealForge Solves
              </h2>
              <p className="text-white/50 text-lg max-w-2xl mx-auto">
                Real estate operators lose deals to friction, delays, and disconnected systems. 
                DealForge infrastructure fixes this.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {problems.map((problem, idx) => {
                const Icon = problem.icon;
                return (
                  <div key={idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-red-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{problem.title}</h3>
                        <p className="text-white/50">{problem.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* DealForge Platform Features */}
        <section className="py-20 px-4 bg-[#050505]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                DealForge Platform Capabilities
              </h2>
              <p className="text-white/50 text-lg max-w-2xl mx-auto">
                DealForge real estate software provides everything you need to execute deals at scale.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-white/50 text-sm">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Who Uses DealForge */}
        <section className="py-20 px-4 border-t border-white/[0.06]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Who Uses DealForge Infrastructure?
              </h2>
              <p className="text-white/50 text-lg max-w-2xl mx-auto">
                DealForge connects the entire real estate deal ecosystem.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {roles.map((role, idx) => {
                const Icon = role.icon;
                return (
                  <div key={idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 text-center hover:border-[#D4AF37]/30 transition-all">
                    <div className="w-14 h-14 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-white/70" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{role.title}</h3>
                    <p className="text-white/50 text-sm">{role.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Infrastructure vs Tool */}
        <section className="py-20 px-4 bg-gradient-to-b from-[#050505] to-black">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              DealForge is Infrastructure, Not Just a Tool
            </h2>
            <p className="text-xl text-white/60 mb-8 leading-relaxed">
              Tools solve one problem. <strong className="text-[#D4AF37]">DealForge infrastructure</strong> connects 
              your entire deal operation—from lead to close to post-close. It's the foundation that 
              scales with you from 10 deals to 100 deals per year.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
                <h3 className="text-lg font-bold text-red-400 mb-4">Traditional Tools</h3>
                <ul className="text-left text-white/50 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="text-red-400">✗</span> Disconnected systems
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-400">✗</span> Manual coordination
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-400">✗</span> Scattered data
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-400">✗</span> Breaks at scale
                  </li>
                </ul>
              </div>
              <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-6">
                <h3 className="text-lg font-bold text-[#D4AF37] mb-4">DealForge Infrastructure</h3>
                <ul className="text-left text-white/50 space-y-2">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D4AF37]" /> Unified platform
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D4AF37]" /> Automated workflows
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D4AF37]" /> Single source of truth
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D4AF37]" /> Built to scale
                  </li>
                </ul>
              </div>
            </div>
            <Link to="/">
              <Button className="gold-gradient-btn h-14 px-10 text-lg font-bold">
                Get DealForge Access
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <DealForgeLogo size="md" showTM={true} />
                <p className="text-white/40 text-sm mt-2">
                  Real Estate Deal Infrastructure
                </p>
              </div>
              
              <div className="flex items-center gap-6">
                <Link to="/" className="text-white/50 hover:text-white text-sm transition-all">
                  Home
                </Link>
                <Link to="/login" className="text-white/50 hover:text-white text-sm transition-all">
                  Login
                </Link>
              </div>
              
              {/* Social Links */}
              <div className="flex items-center gap-4">
                <span className="text-white/30 text-xs">Follow DealForge:</span>
                <a 
                  href="https://www.linkedin.com/company/dealforge%E2%84%A2-patent-pending/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all"
                  aria-label="DealForge on LinkedIn"
                >
                  <svg className="w-4 h-4 text-white/60" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
                <a 
                  href="https://x.com/DealForgeInc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all"
                  aria-label="DealForge on X"
                >
                  <svg className="w-4 h-4 text-white/60" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.facebook.com/share/15eeuUtpPyt/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all"
                  aria-label="DealForge on Facebook"
                >
                  <svg className="w-4 h-4 text-white/60" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-white/[0.06] text-center">
              <p className="text-white/40 text-sm">
                © 2026 DealForge Inc. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
